import { createApi } from "@reduxjs/toolkit/query/react";
import { isEqual } from "lodash";
import { IClause } from "../../interfaces";
import { axiosBaseQuery, QueryArg } from "../axiosConfig";
import { RequestMethod } from "../enum";
import { similarSuggestionRequestBuilder, suggestionRequestBuilder, transformClauses } from "./helpers";
import { SearchSuggestionRequest, SimilarSuggestionSearchRequest } from "./types";

const searchSuggestionQuery = ({ workspaceId, pagination, ...data }: SearchSuggestionRequest): QueryArg => {
  const { filter } = suggestionRequestBuilder(data, true);
  const { merge, ...paginationFilter } = pagination;

  return { url: "", method: RequestMethod.POST, data: { filter: { ...filter, ...paginationFilter } }, workspaceId };
};

const similarSuggestionSearchQuery = ({ workspaceId, ...data }: SimilarSuggestionSearchRequest): QueryArg => {
  const requestBody = similarSuggestionRequestBuilder(data);
  return { url: "", method: RequestMethod.POST, data: requestBody, workspaceId };
};

export const suggestionApi = createApi({
  reducerPath: "suggestionApi",
  baseQuery: axiosBaseQuery<any>({
    baseUrl: "/clauses/suggestions/search",
  }),
  tagTypes: ["Suggestions"],
  endpoints: (build) => ({
    searchSuggestions: build.query<IClause[], SearchSuggestionRequest>({
      query: (args) => searchSuggestionQuery(args),
      transformResponse: transformClauses,

      serializeQueryArgs: ({ queryArgs, endpointName }) => {
        const { pagination, ...restArgs } = queryArgs;
        if (pagination?.merge) {
          return { restArgs, endpointName };
        }
        return { endpointName };
      },
      // Always merge incoming data to the cache entry
      merge: (currentCache, newResponse, { arg: { pagination } }) => {
        if (!pagination?.merge) {
          return newResponse;
        }
        newResponse.forEach((item) => {
          const index = currentCache.findIndex((cl) => cl.clause_id === item.clause_id);
          if (index > -1) {
            currentCache[index] = item;
          } else {
            currentCache.push(item);
          }
        });
        return currentCache;
      },
      // Refetch when the page arg changes
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg?.pagination, previousArg?.pagination);
      },
      keepUnusedDataFor: 36000,
      providesTags: (result, error, { workspaceId }) =>
        result
          ? [
              ...result.map(({ clause_id }) => ({
                type: "Suggestions" as const,
                id: `${clause_id}-${workspaceId}`,
              })),
              { type: "Suggestions", id: workspaceId },
            ]
          : [{ type: "Suggestions", id: workspaceId }],
    }),
    searchSimilarSuggestions: build.query<IClause[], SimilarSuggestionSearchRequest>({
      query: (args) => similarSuggestionSearchQuery(args),
      transformResponse: transformClauses,
    }),
  }),
});
